<template>
  <section
class="pix-payment bg-white rounded p-2 p-md-3 my-2"
data-testid="pix-payment"
>
    <h3>{{ $t('payment.pix.title') }}</h3>
    <p class="text-muted">
{{ $t('payment.pix.description') }}
</p>
    <p
v-if="loading"
role="status"
>
{{ $t('payment.pix.loading') }}
</p>
    <p
v-if="error"
class="alert alert-danger"
role="alert"
>
{{ error }}
</p>
    <div
v-if="!loading && !config.available && !quote"
class="alert alert-info"
>
      {{ $t('payment.pix.unavailable') }}
      <button
class="btn btn-outline-primary ml-1"
type="button"
@click="initialize"
>
{{ $t('payment.pix.retry') }}
</button>
    </div>

    <form
v-if="!loading && config.available && !quote"
@submit.prevent="createPayment"
>
      <label for="pix-amount">{{ $t('payment.pix.amount') }}</label>
      <input
        v-model.number="amount"
id="pix-amount"
class="form-control mb-1"
type="number"
        :min="config.minAmount"
:max="config.maxAmount"
step="0.01"
required
:disabled="busy"
      >
      <small class="d-block text-muted mb-2">{{ $t('payment.pix.limits', { min: money(config.minAmount), max: money(config.maxAmount) }) }}</small>
      <button
class="btn btn-primary"
type="submit"
:disabled="busy"
>
{{ $t(busy ? 'payment.pix.loading' : 'payment.pix.continue') }}
</button>
    </form>

    <div v-if="quote">
      <dl class="row pix-summary mt-2">
        <dt class="col-7">
{{ $t('payment.pix.balanceAmount') }}
</dt><dd class="col-5">
{{ money(quote.toBalanceAmount || quote.amount) }} USD
</dd>
        <template v-if="quote.bankTransferAmount != null">
          <dt class="col-7">
{{ $t('payment.pix.serviceFee') }}
</dt><dd class="col-5">
{{ money(quote.serviceCharge) }} USD
</dd>
          <dt class="col-7">
{{ $t('payment.pix.gatewayFee') }}
</dt><dd class="col-5">
{{ money(quote.paymentGateCharge) }} USD
</dd>
          <dt class="col-7">
{{ $t('payment.pix.tax') }}
</dt><dd class="col-5">
{{ money(quote.taxCharge) }} USD
</dd>
          <dt class="col-7">
{{ $t('payment.pix.totalUsd') }}
</dt><dd class="col-5">
{{ money(quote.bankTransferAmount) }} USD
</dd>
        </template>
        <dt class="col-7">
{{ $t('payment.pix.rate') }}
</dt><dd class="col-5">
1 USD = {{ quote.exchangeRate }} BRL
</dd>
        <dt class="col-7">
{{ $t('payment.pix.totalBrl') }}
</dt><dd class="col-5 font-weight-bold pix-total">
{{ money(quote.brlAmount) }} BRL
</dd>
      </dl>
      <p class="alert alert-info">
{{ $t('payment.pix.iof') }} <a
href="https://www.ebanx.com/pt-br/legal/consumidores/brasil/termos-para-processar-pagamentos/"
target="_blank"
rel="noopener noreferrer"
>{{ $t('payment.pix.terms') }}</a>
</p>

      <form
v-if="status === 'summary'"
@submit.prevent="confirmPayment"
>
        <label for="pix-name">{{ $t('payment.pix.name') }}</label>
        <input
v-model.trim="payer.name"
id="pix-name"
class="form-control mb-1"
autocomplete="name"
required
maxlength="150"
:disabled="busy"
>
        <label for="pix-email">{{ $t('payment.pix.email') }}</label>
        <input
v-model.trim="payer.email"
id="pix-email"
class="form-control mb-1"
type="email"
autocomplete="email"
required
maxlength="254"
:disabled="busy"
>
        <label for="pix-tax-id">{{ $t('payment.pix.taxId') }}</label>
        <input
v-model.trim="payer.taxId"
id="pix-tax-id"
class="form-control mb-2"
inputmode="numeric"
autocomplete="off"
required
maxlength="18"
:disabled="busy"
>
        <button
class="btn btn-primary"
type="submit"
:disabled="busy"
>
{{ $t(busy ? 'payment.pix.loading' : 'payment.pix.pay') }}
</button>
        <button
class="btn btn-outline-secondary ml-1"
type="button"
:disabled="busy || checking"
@click="cancelPayment"
>
{{ $t('payment.pix.cancel') }}
</button>
      </form>

      <div
v-if="status === 'pending'"
aria-live="polite"
>
        <h4>{{ $t('payment.pix.pending') }}</h4>
        <p>{{ $t('payment.pix.waiting') }}</p>
        <div
v-if="quote.brlAmount != null"
class="pix-amount text-center my-2"
>
          <small class="d-block text-muted">{{ $t('payment.pix.payAmountLabel') }}</small>
          <div class="pix-amount-value">R$ {{ money(quote.brlAmount) }}</div>
          <small class="d-block text-muted">{{ $t('payment.pix.payAmountHint') }}</small>
        </div>
        <div v-if="qr">
          <img
v-if="qr.image_url_png"
:src="qr.image_url_png"
:alt="$t('payment.pix.qrAlt')"
class="pix-qr d-block mx-auto my-2"
>
          <label for="pix-code">{{ $t('payment.pix.copyCode') }}</label>
          <textarea
id="pix-code"
:value="qr.data"
class="form-control mb-1"
readonly
rows="3"
/>
          <button
class="btn btn-outline-primary mb-1"
type="button"
@click="copyCode"
>
{{ $t(copied ? 'payment.pix.copied' : 'payment.pix.copy') }}
</button>
          <p>{{ $t(secondsLeft > 0 ? 'payment.pix.expires' : 'payment.pix.expiryWaiting', { seconds: secondsLeft }) }}</p>
        </div>
        <p v-else>
{{ $t('payment.pix.recovering') }}
</p>
        <button
class="btn btn-outline-primary"
type="button"
:disabled="checking"
@click="checkStatus"
>
{{ $t('payment.pix.checkStatus') }}
</button>
        <button
class="btn btn-outline-secondary ml-1"
type="button"
:disabled="busy || checking"
@click="cancelPayment"
>
{{ $t('payment.pix.cancel') }}
</button>
      </div>
      <div
v-if="status === 'done'"
class="alert alert-success"
role="status"
>
{{ $t('payment.pix.done') }}
</div>
      <div
v-if="status === 'rejected'"
class="alert alert-warning"
role="status"
>
{{ $t('payment.pix.rejected') }}
</div>
      <button
v-if="status === 'done' || status === 'rejected'"
class="btn btn-primary"
type="button"
@click="startAgain"
>
{{ $t('payment.pix.newPayment') }}
</button>
    </div>
  </section>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { v4 as uuidv4 } from 'uuid'
import PixService from '@/services/PixService'
import { getResponse } from '@/plugins/store'
import loadPixStripe from '@/libs/pix'

export default {
  data() {
    return {
      loading: true,
      busy: false,
      checking: false,
      config: { available: false },
      amount: null,
      quote: null,
      qr: null,
      payer: { name: '', email: '', taxId: '' },
      status: 'input',
      error: '',
      requestId: null,
      requestAmount: null,
      timer: null,
      destroyed: false,
      copied: false,
      now: Date.now(),
    }
  },
  computed: {
    ...mapGetters('auth', ['user']),
    storageKey() {
      return `gyxen-pix:${this.user?.data?.localUserId || this.user?.data?.email || 'anonymous'}`
    },
    secondsLeft() {
      return Math.max(0, Math.ceil(((this.qr?.expires_at || 0) * 1000 - this.now) / 1000))
    },
  },
  mounted() {
    this.initialize()
  },
  beforeDestroy() {
    this.destroyed = true
    clearTimeout(this.timer)
  },
  methods: {
    ...mapActions('auth', ['getAccountInfo', 'getGeneralConfig']),
    ...mapActions('payment', ['getHistoryLimit']),
    money(value) {
      return new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value) || 0)
    },
    savedTransaction(value) {
      try {
        if (value === null) sessionStorage.removeItem(this.storageKey)
        else if (value !== undefined) sessionStorage.setItem(this.storageKey, value)
        else return sessionStorage.getItem(this.storageKey)
      } catch (e) {
        // Payments still work when browser storage is unavailable.
      }
      return null
    },
    async initialize() {
      this.loading = true
      this.error = ''
      this.payer.name = this.user?.data?.fullName || ''
      this.payer.email = this.user?.data?.email || ''
      try {
        this.config = getResponse((await PixService.getConfig()).data)
        if (!this.amount) this.amount = this.config.minAmount
      } catch (e) {
        this.error = e?.data?.message || this.$t('payment.pix.networkError')
      }
      let transactionId = this.savedTransaction()
      if (!transactionId) {
        // Resume an unfinished payment created in another session or device.
        try {
          const pending = getResponse((await PixService.getPendingTransaction()).data)
          transactionId = pending?.transactionId || null
          if (transactionId) this.savedTransaction(transactionId)
        } catch (e) {
          // The payment form still works without a resumed payment.
        }
      }
      if (transactionId) {
        this.quote = { transactionId }
        this.status = 'pending'
        await this.checkStatus()
      }
      this.loading = false
    },
    async createPayment() {
      if (this.busy || this.quote) return
      this.error = ''
      if (!this.config.available || !Number.isFinite(Number(this.amount)) || Number(this.amount) <= 0 || Number(this.amount) < this.config.minAmount || Number(this.amount) > this.config.maxAmount) {
        this.error = this.$t('payment.pix.invalidAmount')
        return
      }
      this.busy = true
      if (!this.requestId || this.requestAmount !== Number(this.amount)) {
        this.requestId = uuidv4()
        this.requestAmount = Number(this.amount)
      }
      try {
        this.quote = getResponse((await PixService.requestPayment({ amount: Number(this.amount), requestId: this.requestId })).data)
        this.savedTransaction(this.quote.transactionId)
        this.status = 'summary'
      } catch (e) {
        this.error = e?.data?.message || this.$t('payment.pix.networkError')
      } finally {
        this.busy = false
      }
    },
    async confirmPayment() {
      if (this.busy || !this.quote?.clientSecret) return
      this.error = ''
      const taxId = this.payer.taxId.replace(/\D/g, '')
      if (!this.payer.name.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.payer.email) || ![11, 14].includes(taxId.length)) {
        this.error = this.$t('payment.pix.invalidPayer')
        return
      }
      this.busy = true
      try {
        const stripe = await loadPixStripe(this.config.stripePk)
        const result = await stripe.confirmPixPayment(this.quote.clientSecret, {
          payment_method: { billing_details: { name: this.payer.name, email: this.payer.email, tax_id: taxId } },
          return_url: `${window.location.origin}/payment/add-fund?tab=pix`,
        }, { handleActions: false })
        if (this.destroyed) return
        if (result.error) {
          this.error = result.error.message || this.$t('payment.pix.confirmError')
          // A network error can happen after Stripe accepted confirmation: reconcile first.
          await this.checkStatus()
          return
        }
        this.qr = result.paymentIntent?.next_action?.pix_display_qr_code || null
        this.payer.taxId = ''
        this.status = 'pending'
        await this.checkStatus()
      } catch (e) {
        this.error = this.$t('payment.pix.confirmError')
        await this.checkStatus()
      } finally {
        this.busy = false
      }
    },
    async checkStatus() {
      if (this.checking || this.destroyed || !this.quote?.transactionId || ['done', 'rejected'].includes(this.status)) return
      clearTimeout(this.timer)
      this.checking = true
      try {
        const result = getResponse((await PixService.getTransaction(this.quote.transactionId)).data)
        if (this.destroyed) return
        this.quote = { ...this.quote, ...result }
        if (result.qr) this.qr = result.qr
        if (result.status === 'done') {
          this.status = 'done'
          this.error = ''
          this.savedTransaction(null)
          // Payment remains successful even if refreshing another dashboard endpoint fails.
          await Promise.allSettled([
            this.getAccountInfo(), this.getGeneralConfig(),
            this.getHistoryLimit({ page: 1, size: 10, types: ['add_fund_user_balance'] }),
          ])
        } else if (result.status === 'rejected') {
          this.status = 'rejected'
          this.savedTransaction(null)
        } else if (['requires_payment_method', 'requires_confirmation'].includes(result.paymentStatus) && result.clientSecret) {
          this.status = 'summary'
        } else {
          this.status = 'pending'
        }
      } catch (e) {
        this.error = e?.data?.message || this.$t('payment.pix.networkError')
        if (e?.status === 404 || e?.status === 403) {
          this.savedTransaction(null)
          this.quote = null
          this.status = 'input'
        }
      } finally {
        this.now = Date.now()
        this.checking = false
        if (!this.destroyed && this.quote && this.status === 'pending') this.timer = setTimeout(this.checkStatus, 5000)
      }
    },
    async copyCode() {
      try {
        await navigator.clipboard.writeText(this.qr.data)
        this.copied = true
      } catch (e) {
        this.error = this.$t('payment.pix.copyManually')
      }
    },
    startAgain() {
      if (!['done', 'rejected'].includes(this.status)) return
      this.resetPayment()
    },
    async cancelPayment() {
      if (this.busy || this.checking || !this.quote?.transactionId) return
      this.busy = true
      this.error = ''
      clearTimeout(this.timer)
      try {
        await PixService.cancelTransaction(this.quote.transactionId)
      } catch (e) {
        // A missing transaction is already gone; any other failure keeps the payment and re-syncs its status.
        if (e?.status !== 404) {
          this.error = e?.data?.message || this.$t('payment.pix.networkError')
          this.busy = false
          await this.checkStatus()
          return
        }
      }
      this.busy = false
      this.resetPayment()
    },
    resetPayment() {
      clearTimeout(this.timer)
      this.savedTransaction(null)
      this.quote = null
      this.qr = null
      this.requestId = null
      this.requestAmount = null
      this.error = ''
      this.copied = false
      this.status = 'input'
    },
  },
}
</script>

<style scoped>
.pix-payment { max-width: 650px; margin-left: auto; margin-right: auto; }
.pix-summary dd { text-align: right; }
.pix-qr { width: 240px; max-width: 100%; }
.pix-total { font-size: 1.15rem; }
.pix-amount-value { font-size: 2rem; font-weight: 700; line-height: 1.2; }
</style>
